import { api } from "@/api";
import type {
  MailConversation,
  MailDirectionFilter,
  MailListView,
  MailMessage,
  MailPage,
  MailSource,
  MailTaskFilter,
} from "./types";

export type MailListQuery = {
  view: MailListView;
  direction: MailDirectionFilter;
  tasks: MailTaskFilter;
  search?: string;
  cursor?: string | null;
};

/**
 * The list endpoint for a source, as a plain string so hooks can depend on it without
 * re-running every time a new source object is created.
 */
export const mailListPath = (source: MailSource): string =>
  source.kind === "customer"
    ? `/mail/customers/${source.companyId}/messages`
    : "/mail/mine/messages";

export const getMessages = async (listPath: string, query: MailListQuery): Promise<MailPage> => {
  const response = await api.get<MailPage>(listPath, {
    params: {
      view: query.view,
      direction: query.direction === "all" ? undefined : query.direction,
      tasks: query.tasks === "all" ? undefined : query.tasks,
      search: query.search || undefined,
      cursor: query.cursor || undefined,
    },
  });
  return response.data;
};

/**
 * Resolves to null when the message does not exist or the user's CRM restrictions hide it.
 * The BFF reports that as a 404, which is accepted here so the shared error interceptor
 * doesn't turn it into a generic failure.
 */
export const getMessage = async (mailId: string): Promise<MailMessage | null> => {
  const response = await api.get<MailMessage | null>(`/mail/messages/${mailId}`, {
    validateStatus: status => status === 200 || status === 404,
  });
  return response.status === 404 ? null : response.data;
};

export type DeleteMessageResult = { status: "deleted" } | { status: "hasTasks"; message: string };

/**
 * Soft deletes an email so it leaves every list and is never synced again. A 404 means it is
 * already deleted or hidden, which is treated as deleted so it simply leaves the list. A 409
 * means it still has tasks, some of which may be hidden from the user, with the API's explanation.
 */
export const deleteMessage = async (mailId: string): Promise<DeleteMessageResult> => {
  const response = await api.delete<{ message?: string | null } | null>(`/mail/messages/${mailId}`, {
    validateStatus: status => status === 200 || status === 404 || status === 409,
  });
  if (response.status !== 409) return { status: "deleted" };

  return {
    status: "hasTasks",
    message: response.data?.message
      || "This email has linked tasks, so it can't be deleted. Delete the tasks first, then delete the email.",
  };
};

/**
 * The conversation an email belongs to, as seen from the source's list. Resolves to null on a
 * 404, like getMessage.
 */
export const getConversation = async (mailId: string, source: MailSource): Promise<MailConversation | null> => {
  const response = await api.get<MailConversation | null>(`/mail/messages/${mailId}/conversation`, {
    params: { companyId: source.kind === "customer" ? source.companyId : undefined },
    validateStatus: status => status === 200 || status === 404,
  });
  return response.status === 404 ? null : response.data;
};
